import { useCallback, useEffect, useState } from 'react'
import { advisorDraftManifest, packPreviewRows, type PackManifest } from './packAuthoring'
import type { ViewsApi } from './viewsApi'
import './views.css'

type PackEntry = { source: string; manifest: PackManifest }
type Template = { pack_template_id: string; name: string; description: string; version: number; status: string; manifest: PackManifest }

/** Firm admin → Templates: draft, publish, restore and retire advisor pack templates. */
export function TemplatesPage({ api, workbookId }: { api: ViewsApi; workbookId: string | null }) {
  const [packs, setPacks] = useState<PackEntry[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [source, setSource] = useState('')
  const [fields, setFields] = useState({ packKey: '', name: '', description: '' })
  const [selected, setSelected] = useState<string>('')
  const [status, setStatus] = useState<{ tone: 'ok' | 'error' | 'busy'; text: string } | null>(null)

  const load = useCallback(async () => {
    if (!workbookId) return
    const [nextPacks, nextTemplates] = await Promise.all([api.packs(workbookId), api.packTemplates(workbookId)])
    setPacks(nextPacks as PackEntry[])
    setTemplates(nextTemplates as Template[])
  }, [api, workbookId])

  useEffect(() => {
    load().catch((e) => setStatus({ tone: 'error', text: String(e?.message || e) }))
  }, [load])

  const run = async (label: string, action: () => Promise<unknown>) => {
    setStatus({ tone: 'busy', text: `${label}…` })
    try {
      await action()
      await load()
      setStatus({ tone: 'ok', text: `${label}: done.` })
    } catch (e) {
      setStatus({ tone: 'error', text: e instanceof Error ? e.message : String(e) })
    }
  }

  const template = templates.find((t) => t.pack_template_id === selected) || null
  const sourceManifest = packs.find((p) => `${p.manifest.pack_key}:${p.manifest.version}` === source)?.manifest

  if (!workbookId) return <p className="fg-views-muted">Select a company to manage its templates.</p>
  return (
    <div className="fg-views">
      <section className="fg-views-panel" aria-label="New template">
        <h3>New template from a pack</h3>
        <div className="fg-views-row">
          <select aria-label="Source pack" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Choose a source pack…</option>
            {packs.map((p) => <option key={`${p.manifest.pack_key}:${p.manifest.version}`} value={`${p.manifest.pack_key}:${p.manifest.version}`}>{p.manifest.name} v{p.manifest.version}</option>)}
          </select>
          <input aria-label="Pack key" placeholder="firm.monthly_close" value={fields.packKey} onChange={(e) => setFields({ ...fields, packKey: e.target.value })} />
          <input aria-label="Template name" placeholder="Name" value={fields.name} onChange={(e) => setFields({ ...fields, name: e.target.value })} />
          <input aria-label="Template description" placeholder="Description" value={fields.description} onChange={(e) => setFields({ ...fields, description: e.target.value })} />
          <button type="button" className="btn primary" onClick={() => void run('Creating draft', () => api.createPackDraft(workbookId, advisorDraftManifest(sourceManifest, fields)))}>Create draft</button>
        </div>
      </section>
      <section className="fg-views-panel" aria-label="Templates">
        <h3>Templates</h3>
        <select aria-label="Template version" value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Choose a version…</option>
          {templates.map((t) => <option key={t.pack_template_id} value={t.pack_template_id}>{t.name} v{t.version} · {t.status}</option>)}
        </select>
        {template && (
          <>
            <table className="fg-views-table"><tbody>{packPreviewRows(template.manifest).map(([k, v]) => <tr key={k}><th>{k}</th><td>{v}</td></tr>)}</tbody></table>
            <div className="fg-views-row">
              <button type="button" className="btn primary" disabled={template.status !== 'draft'} onClick={() => void run('Publishing', () => api.publishPack(workbookId, template.pack_template_id))}>Publish</button>
              <button type="button" className="btn secondary" disabled={!['published', 'retired'].includes(template.status)} onClick={() => void run('Restoring as a new version', () => api.rollbackPack(workbookId, template.pack_template_id))}>Restore as new version</button>
              <button type="button" className="btn secondary" disabled={template.status !== 'published'} onClick={() => void run('Retiring', () => api.retirePack(workbookId, template.pack_template_id))}>Retire</button>
            </div>
          </>
        )}
      </section>
      {status && <p className={`fg-views-status ${status.tone}`} role="status">{status.text}</p>}
    </div>
  )
}

export default TemplatesPage
